import React, { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { TextArea } from "../../../components/forms/TextArea";
import { QuestionWithRelations } from "../../../../../api/src/types";
import { validateTextWithRestriction } from "../utils/questionnaire";
import { useGetCurrentUserQuery } from "../../../api/users";
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from "../../../api/answers";
import { QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT } from "../../../components/theme";
import { ErrorWrapper, InputWrapper } from "./SharedFormStyles";

interface TextAreaInputProps {
  question: QuestionWithRelations;
}

const TextAreaInput: React.FC<TextAreaInputProps> = ({ question }) => {
  const [textAreaInput, setTextAreaInput] = useState<string | null>(
    question.answers[0]?.valueTyped ?? null,
  );
  const [textAreaError, setTextAreaError] = useState<string | null>(null);

  const { data: user } = useGetCurrentUserQuery();
  const [
    createAnswer,
    {
      /*isLoading: isCreating,*/
      isError: isCreateError,
      /*isSuccess: isCreateSuccess,*/
      reset: resetCreateAnswer,
    },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    {
      /*isLoading: isUpdating,*/
      isError: isUpdateError,
      /*isSuccess: isUpdateSuccess,*/
      reset: resetUpdateAnswer,
    },
  ] = useUpdateAnswerMutation();

  const hasAnswer = question.answers.length > 0;

  const save = useDebouncedCallback((value: string | null) => {
    if (!user) return;

    if (!hasAnswer && !value) return;

    const payload = {
      questionId: question.id,
      userId: user.id,
      valueTyped: value,
    };

    if (!hasAnswer) void createAnswer(payload);
    else void updateAnswer(payload);
  }, 1000);

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    resetCreateAnswer();
    resetUpdateAnswer();

    const value = e.target.value;
    setTextAreaInput(value);

    // Validate text area input with restrictions
    const error = validateTextWithRestriction(value, question);
    setTextAreaError(error);

    if (error) {
      save(null);
      return;
    }
    save(value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const trimmedValue = e.target.value.trim();

    // Update local state for immediate visual feedback; backend will handle trimming on save
    if (trimmedValue !== textAreaInput) {
      setTextAreaInput(trimmedValue);
    }
  };

  //const isLoading = isCreating || isUpdating;
  const hasError = isCreateError || isUpdateError;
  /*const isSuccess =
    (isCreateSuccess || isUpdateSuccess) &&
    !hasError &&
    !isLoading &&
    !textAreaError &&
    textAreaInput !== "";
  */
  return (
    <>
      <InputWrapper>
        <TextArea
          placeholder={QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT}
          value={textAreaInput ?? ""}
          onChange={(e) => {
            handleTextAreaChange(e);
          }}
          onBlur={handleBlur}
          rows={4}
          required={question.isRequired}
          errorMessage={textAreaError}
        />
        {/* Commenting out the loader and success icon for now as per the request of the design team */}
        {/*
        {isLoading && (
          <IconContainer>
            <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
          </IconContainer>
        )}
        {isSuccess && (
          <IconContainer>
            <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
          </IconContainer>
        )}
        */}
      </InputWrapper>
      {hasError && (
        <ErrorWrapper>
          Error saving answer, please refresh the page and try again
        </ErrorWrapper>
      )}
    </>
  );
};

export default TextAreaInput;
